import React, { useState, useRef } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { phoneParts } from "@/lib/phone";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PhoneCall, ArrowLeft, CreditCard, Search, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function RicaricaCliente() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("IT");
  const [op, setOp] = useState(null);
  const [amount, setAmount] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const detectionRequest = useRef(0);
  const clearDetection = () => {
    detectionRequest.current += 1;
    setDetecting(false); setOp(null); setAmount(""); setError("");
  };

  const detect = async () => {
    if (!phone.trim()) { toast.error("Inserisci il numero con prefisso (es. +39…)"); return; }
    const request = ++detectionRequest.current;
    setDetecting(true); setOp(null); setAmount(""); setError("");
    try {
      const { data } = await api.get("/reloadly/airtime/detect", { params: { phone: phone.trim(), country } });
      if (request !== detectionRequest.current) return;
      setOp(data);
      toast.success(`Operatore rilevato: ${data.name || data.operatorId}`);
    } catch (e) {
      if (request !== detectionRequest.current) return;
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Operatore non rilevato";
      setError(message); toast.error(message);
    } finally { if (request === detectionRequest.current) setDetecting(false); }
  };

  const fixed = (op?.fixedAmounts || []).filter((n) => n > 0);
  const amountValue = Number(amount);
  const validAmount = amountValue > 0 && Number.isFinite(amountValue) && Number(amountValue.toFixed(2)) === amountValue
    && (op?.denominationType === "FIXED" ? fixed.some((v) => Number(v) === amountValue)
      : op?.denominationType === "RANGE" && op.minAmount != null && op.maxAmount != null
        && amountValue >= Number(op.minAmount) && amountValue <= Number(op.maxAmount));

  const pay = async () => {
    if (paying || detecting) return;
    const amt = parseFloat(amount);
    if (!op?.operatorId || !phone.trim()) { toast.error("Rileva prima l'operatore per questo numero"); return; }
    if (!validAmount) { toast.error("Scegli un importo valido tra quelli consentiti dall'operatore"); return; }
    setPaying(true); setError("");
    try {
      const { data } = await api.post("/reloadly/airtime/customer-checkout", {
        operatorId: op.operatorId, amount: amt, countryCode: country, number: phone.trim(), operatorName: op.name,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento";
      if (["OPERATOR_MISMATCH", "OPERATOR_COUNTRY_UNVERIFIED", "AMOUNT_VERIFICATION_UNAVAILABLE", "INVALID_AMOUNT"].includes(e.response?.data?.detail?.code)) clearDetection();
      setError(message); toast.error(message); setPaying(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="ricarica-cliente-page">
      <button onClick={() => navigate("/app/servizi")} className="app-text-button flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="ricarica-back"><ArrowLeft className="w-4 h-4" /> Servizi</button>
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Ricarica telefonica</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><PhoneCall className="w-7 h-7 text-teal-500" /> Ricarica un numero</h1>
        <p className="text-sm text-muted-foreground mt-1">Inserisci il numero: Reloadly verifica l'operatore prima del pagamento e ricontrolla prima di inviare la ricarica.</p>
      </div>

      <Card className="p-6 glow-border space-y-4">
        <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
          <div>
            <Label>Numero di telefono (con prefisso)</Label>
            {phone && !country && <p className="text-xs text-muted-foreground" data-testid="ricarica-country-unavailable">Numero senza Paese: non supportato per le ricariche mobili.</p>}
            <Input type="tel" country={country} onCountryChange={(value) => { clearDetection(); setCountry(value); }} className="mt-1.5" data-testid="ricarica-phone" value={phone} disabled={paying} onChange={(e) => { clearDetection(); setPhone(e.target.value); }} />
          </div>
          <Button onClick={detect} disabled={detecting || paying || !phone.trim() || !country || !phoneParts(phone, country).national.replace(/\D/g, "")} className="gap-2" data-testid="ricarica-detect">
            {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Rileva operatore
          </Button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="ricarica-verification-notice">Se numero e operatore non coincidono o Reloadly non riesce a verificarli, non apriamo il pagamento. Il rilevamento standard non certifica la titolarità o l'operatore dopo una portabilità.</p>
        {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500" data-testid="ricarica-verification-error">{error}</p>}

        {op && (
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="ricarica-operator">
            <div className="flex items-center gap-3">
              {op.logoUrls?.[0] && <img src={op.logoUrls[0]} alt={op.name} className="w-10 h-10 rounded object-contain bg-white/5 p-1" />}
              <p className="font-semibold" data-testid="ricarica-operator-name">{op.name}</p>
            </div>
            <div>
              <Label>Importo</Label>
              {fixed.length ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {fixed.map((d) => (
                    <Button key={d} type="button" disabled={paying} size="sm" variant={String(d) === amount ? "default" : "outline"} onClick={() => setAmount(String(d))} data-testid={`ricarica-amt-${d}`}>{euro(d)}</Button>
                  ))}
                </div>
              ) : (
                <Input type="number" step="0.01" min={op.minAmount} max={op.maxAmount} disabled={paying} className="mt-1.5" data-testid="ricarica-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="es. 10" />
              )}
            </div>
            <Button onClick={pay} disabled={paying || detecting || !validAmount} className="gap-2 w-full" data-testid="ricarica-pay">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {paying ? "Verifica in corso…" : `Verifica e paga ${amount ? euro(parseFloat(amount)) : ""}`}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
